import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { createAnalysis } from "@/lib/api";
import { ArrowRight, ShieldCheck, Lightning, MagnifyingGlassPlus, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Intake() {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [context, setContext] = useState("");

  const mutation = useMutation({
    mutationFn: () => createAnalysis(url.trim(), context.trim()),
    onSuccess: (data) => {
      toast.success("Analysis queued");
      navigate(`/analysis/${data.id}`);
    },
    onError: (err) => {
      const msg = err?.response?.data?.detail || err?.message || "Could not start analysis";
      toast.error(msg);
    },
  });

  const submit = (e) => {
    e.preventDefault();
    if (!url.trim()) {
      toast.error("Paste a SaaS URL to start");
      return;
    }
    mutation.mutate();
  };

  return (
    <div className="flex-1 min-h-screen flex flex-col" data-testid="intake-page">
      {/* Hero */}
      <div className="relative border-b border-zinc-200 tech-grid-bg">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 pt-20 pb-24">
          <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-6">
            01 / Intake · Reverse-analysis engine
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-zinc-950 leading-[0.95]">
            Read any SaaS<br />
            like a build spec.
          </h1>
          <p className="mt-6 max-w-2xl text-zinc-600 text-base sm:text-lg leading-relaxed">
            Paste a URL. TeardownIQ crawls the public surface, fingerprints the stack,
            classifies the industry, and returns a prioritised build spec — weighted by
            the constraints of that vertical. No auth bypass. No guessing the backend.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Link
              to="/studio"
              data-testid="iq-studio-hero-button"
              className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-emerald-700 transition-colors"
            >
              <Sparkle size={14} weight="fill" /> IQ Studio — combine 3 products into one
            </Link>
            <span className="font-mono text-[11px] text-zinc-500">
              Stack up to 3 teardowns, synthesise a super-product, export an Emergent / Claude Code plan + PRD.
            </span>
          </div>

          <form onSubmit={submit} className="mt-12 space-y-4" data-testid="intake-form">
            <div className="flex border border-zinc-950 bg-white">
              <div className="flex items-center px-4 border-r border-zinc-300 font-mono text-xs text-zinc-500 uppercase tracking-wider">
                https://
              </div>
              <Input
                data-testid="analyze-url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value.replace(/^https?:\/\//i, ""))}
                placeholder="linear.app  ·  notion.so  ·  vercel.com"
                className="sharp flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 font-mono text-base h-14 px-4"
              />
              <button
                type="submit"
                data-testid="analyze-submit-button"
                disabled={mutation.isPending}
                className="bg-zinc-950 text-white px-6 sm:px-8 flex items-center gap-2 font-mono text-xs uppercase tracking-widest hover:bg-zinc-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {mutation.isPending ? "Queuing…" : "Analyze"}
                <ArrowRight size={16} weight="bold" />
              </button>
            </div>

            <div className="border border-zinc-200 bg-white">
              <div className="px-4 py-2 border-b border-zinc-200 font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center justify-between">
                <span>Optional · Builder context</span>
                <span>{context.length}/2000</span>
              </div>
              <Textarea
                data-testid="builder-context-input"
                value={context}
                onChange={(e) => setContext(e.target.value.slice(0, 2000))}
                placeholder={"I'm building in this space. I care about X. The thing I want to do differently is Y…"}
                rows={4}
                className="sharp border-0 focus-visible:ring-0 focus-visible:ring-offset-0 font-mono text-sm resize-none"
              />
            </div>
          </form>
        </div>
      </div>

      {/* What you'll get strip */}
      <div className="border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-200">
          {[
            {
              n: "01",
              icon: <MagnifyingGlassPlus size={20} weight="regular" />,
              title: "Inferred system map",
              body: "Tech detected from headers, cookies, scripts and DNS. Every item tagged observed or inferred, with a confidence level. We never present a guess as a fact.",
            },
            {
              n: "02",
              icon: <Lightning size={20} weight="regular" />,
              title: "Industry-weighted spec",
              body: "Recommendations are filtered through a seeded lens for the target's vertical — fintech gets audit trails, dev tools get docs depth, e-commerce gets conversion levers.",
            },
            {
              n: "03",
              icon: <ShieldCheck size={20} weight="regular" />,
              title: "Compliant by default",
              body: "We respect robots.txt, rate-limit politely, cap pages crawled per run, and never bypass authentication. Public surface only.",
            },
          ].map((c) => (
            <div key={c.n} className="bg-white p-7">
              <div className="flex items-center justify-between mb-4">
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{c.n}</span>
                <span className="text-zinc-700">{c.icon}</span>
              </div>
              <div className="font-display text-lg font-semibold text-zinc-950 mb-2 tracking-tight">
                {c.title}
              </div>
              <div className="text-sm text-zinc-600 leading-relaxed">{c.body}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Pipeline preview */}
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16">
        <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-3">
          02 / Pipeline
        </div>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold text-zinc-950 tracking-tight mb-8">
          Seven stages. One run.
        </h2>
        <div className="border border-zinc-200">
          {[
            ["01", "Crawl & collect", "homepage · pricing · features · docs · changelog · careers · security"],
            ["02", "Tech fingerprinting", "Wappalyzer-style rules across headers, cookies, scripts"],
            ["03", "Business understanding", "vertical, ICP, pricing model, stage, positioning"],
            ["04", "Feature inventory", "structured list of current product capabilities"],
            ["05", "Industry-sensitivity lens", "10-vertical seeded config weighting recommendations"],
            ["06", "Build-spec synthesis", "prioritised BuildItems with evidence and build-vs-buy"],
            ["07", "Report & export", "on-screen report + Markdown + feature-spec exports"],
          ].map(([n, t, d], idx, arr) => (
            <div
              key={n}
              className={`grid grid-cols-[60px_220px_1fr] gap-4 px-5 py-4 ${
                idx !== arr.length - 1 ? "border-b border-zinc-200" : ""
              }`}
            >
              <div className="font-mono text-xs text-zinc-500">{n}</div>
              <div className="font-display font-medium text-zinc-950">{t}</div>
              <div className="font-mono text-xs text-zinc-500 leading-relaxed">{d}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
